import React, { useCallback, useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { buildLeadClipboardText } from '../../../shared/lib/leadClipboard';
import type { Control, FieldErrors, UseFormGetValues, UseFormRegister, UseFormSetValue } from 'react-hook-form';
import { InputMask } from '@react-input/mask';
import {
  IconCheck,
  IconCopy,
  IconFileDescription,
  IconLock,
  IconMapPin,
  IconPhone,
  IconRoute,
  IconRuler2,
  IconUser,
} from '@tabler/icons-react';

import type { LeadFormValues } from '../../create-lead/model/leadForm';
import { validateLeadArrival, validateOptionalPhone } from '../../create-lead/model/leadForm';
import type { CardModalFilesController } from '../model/cardModalTypes';
import { PHONE_MASK_OPTIONS } from '../../../shared/lib/phone';
import { CitySelect } from '../../../shared/ui/CitySelect';
import { DateTimeTzInput } from '../../../shared/ui/DateTimeTzInput';
import { FileInput } from '../../../shared/ui/FileInput';
import { FormField } from '../../../shared/ui/FormField';
import { NumberInput } from '../../../shared/ui/NumberInput';
import { TextArea } from '../../../shared/ui/Textarea';
import { TextInput } from '../../../shared/ui/TextInput';
import { Select } from '../../../shared/ui/Select';
import { fetchPartnersOptions } from '../../../entities/partner';
import { SmsButton } from '../../card-sms';
import { useAuth } from '../../auth/model/authStore';
import { UserSearchInput } from '../../../shared/ui/UserSearchInput/UserSearchInput';

interface Props {
  isAdmin: boolean;
  hidden: boolean;
  loading: boolean;
  leadId: string;
  leadOwnerId?: string;
  control: Control<LeadFormValues>;
  register: UseFormRegister<LeadFormValues>;
  errors: FieldErrors<LeadFormValues>;
  getValues: UseFormGetValues<LeadFormValues>;
  setValue: UseFormSetValue<LeadFormValues>;
  files: CardModalFilesController;
  onClose: () => void;
  onSave: React.FormEventHandler<HTMLFormElement>;
  onDelete?: () => void;
  onNotify: (msg: string) => void;
}

const FULL_WIDTH_STYLE: React.CSSProperties = { gridColumn: '1/-1' };

const CardModalInfoPane: React.FC<Props> = ({
  isAdmin,
  hidden,
  loading,
  leadId,
  control,
  register,
  errors,
  getValues,
  setValue,
  files,
  onClose,
  onSave,
  onDelete,
  onNotify,
}) => {
  const { currentUser } = useAuth();
  const canEditSource = currentUser?.role === 'ADMIN' || currentUser?.role === 'MANAGER';
  const userName = useWatch({ control, name: 'userName' });
  const partnerName = useWatch({ control, name: 'partnerName' });
  const [copiedNote, setCopiedNote] = useState(false);

  const handleCopyNote = useCallback(async () => {
    const v = getValues();
    try {
      await navigator.clipboard.writeText(buildLeadClipboardText({
        title: v.title ?? '',
        phone: v.phone ?? '',
        destination: v.destination ?? '',
        city: v.city ?? '',
        sending: v.sending ?? '',
        note: v.note ?? '',
      }));
      setCopiedNote(true);
      window.setTimeout(() => setCopiedNote(false), 1500);
    } catch {
      onNotify('Не удалось скопировать примечание');
    }
  }, [getValues, onNotify]);

  return (
    <form
      className={`cm-r${hidden ? ' cm-tab-hidden' : ''}`}
      style={loading ? { visibility: 'hidden' } : undefined}
      onSubmit={onSave}
    >
      <div className="cm-r-scroll">
        <div className="sec">Данные клиента</div>
        <div className="inf-grid">
          <FormField className="inf-field" label="ФИО" style={FULL_WIDTH_STYLE} error={errors.title?.message}>
            <TextInput
              className="inf-input"
              controlClassName="control-with-icon"
              icon={<IconUser size={16} stroke={1.9} />}
              {...register('title', {
                required: 'Укажите ФИО клиента',
                validate: v => v.trim() !== '' || 'Укажите ФИО клиента',
              })}
            />
          </FormField>

          <FormField className="inf-field" label="Исполнитель" style={FULL_WIDTH_STYLE}>
            <Controller
              control={control}
              name="userId"
              render={({ field }) => (
                <UserSearchInput
                  value={field.value}
                  selectedName={userName ?? ''}
                  onSelect={(id, name) => { field.onChange(id); setValue('userName', name); }}
                  onClear={() => { field.onChange(''); setValue('userName', ''); }}
                  placeholder="Поиск исполнителя"
                />
              )}
            />
          </FormField>

          <FormField className="inf-field" label="Телефон" error={errors.phone?.message}>
            <Controller
              control={control}
              name="phone"
              rules={{ validate: validateOptionalPhone }}
              render={({ field }) => (
                <div className="control-with-icon">
                  <span className="field-icon" aria-hidden="true">
                    <IconPhone size={16} stroke={1.9} />
                  </span>
                  <InputMask
                    className="inf-input"
                    mask={PHONE_MASK_OPTIONS.mask}
                    replacement={PHONE_MASK_OPTIONS.replacement}
                    placeholder="+7 (___) ___-__-__"
                    type="tel"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                </div>
              )}
            />
          </FormField>

          <FormField className="inf-field" label="Возраст" error={errors.age?.message}>
            <NumberInput
              className="inf-input"
              controlClassName="control-with-icon"
              icon={<IconRuler2 size={16} stroke={1.9} />}
              min="0"
              {...register('age', {
                validate: v => v === '' || Number(v) >= 0 || 'Возраст не может быть отрицательным',
              })}
            />
          </FormField>

          <FormField className="inf-field" label="Город выезда" style={FULL_WIDTH_STYLE}>
            <Controller
              control={control}
              name="city"
              render={({ field }) => (
                <CitySelect
                  value={field.value}
                  onChange={field.onChange}
                  icon={<IconMapPin size={16} stroke={1.9} />}
                />
              )}
            />
          </FormField>

          <FormField className="inf-field" label="Город назначения" style={FULL_WIDTH_STYLE}>
            <Controller
              control={control}
              name="destination"
              render={({ field }) => (
                <CitySelect
                  value={field.value}
                  onChange={field.onChange}
                  icon={<IconRoute size={16} stroke={1.9} />}
                />
              )}
            />
          </FormField>

          <FormField className="inf-field" label="Отправление — местные дата и время" style={FULL_WIDTH_STYLE}>
            <Controller
              control={control}
              name="sending"
              render={({ field }) => (
                <DateTimeTzInput
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                />
              )}
            />
          </FormField>

          <FormField className="inf-field" label="Прибытие — местные дата и время" style={FULL_WIDTH_STYLE} error={errors.arrival?.message}>
            <Controller
              control={control}
              name="arrival"
              rules={{ validate: v => validateLeadArrival(v, getValues('sending')) }}
              render={({ field }) => (
                <DateTimeTzInput
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                />
              )}
            />
          </FormField>

          <FormField className="inf-field" label="Предписание" style={FULL_WIDTH_STYLE}>
            <Controller
              control={control}
              name="orderReceived"
              render={({ field }) => (
                <button
                  type="button"
                  className={`asgn-allday-toggle${field.value ? ' active' : ''}`}
                  onClick={() => field.onChange(!field.value)}
                >
                  <div className="asgn-allday-cb">
                    {field.value && <IconCheck size={9} stroke={3} />}
                  </div>
                  Предписание получено
                </button>
              )}
            />
          </FormField>

        </div>

        <div className="sec">Источник привлечения</div>
        <div className="inf-grid">
          <FormField className="inf-field" label="Партнер">
            <Controller
              control={control}
              name="partnerId"
              render={({ field }) => (
                <Select
                  fetchOptions={fetchPartnersOptions}
                  options={field.value && partnerName ? [{ value: field.value, label: partnerName }] : []}
                  value={field.value ?? ''}
                  onChange={(val, opt) => {
                    field.onChange(val);
                    setValue('partnerName', opt?.label ?? '');
                  }}
                  placeholder="Выберите партнера"
                />
              )}
            />
          </FormField>

          <FormField className="inf-field" label="Источник">
            <TextInput
              className={canEditSource ? 'inf-input' : 'inf-input inf-input--locked'}
              controlClassName={canEditSource ? undefined : 'inf-input-locked-wrap'}
              icon={canEditSource ? undefined : <IconLock size={13} stroke={2} />}
              iconClassName={canEditSource ? undefined : 'inf-input-lock-icon'}
              placeholder="—"
              {...register('source')}
              readOnly={!canEditSource}
            />
          </FormField>
        </div>

        <div className="inf-grid">
          <FormField
            className="inf-field"
            label="Примечание"
            style={FULL_WIDTH_STYLE}
            labelSuffix={
              <button
                type="button"
                className={`inf-note-copy${copiedNote ? ' copied' : ''}`}
                onClick={handleCopyNote}
              >
                {copiedNote ? <IconCheck size={11} stroke={2.5} /> : <IconCopy size={11} stroke={2} />}
                {copiedNote ? 'Скопировано' : 'Скопировать'}
              </button>
            }
          >
            <TextArea
              className="inf-textarea"
              controlClassName="textarea-with-icon"
              icon={<IconFileDescription size={16} stroke={1.9} />}
              placeholder="Добавьте важную информацию по клиенту"
              {...register('note')}
            />
          </FormField>
        </div>

        <div className="sec">Вложения</div>
        <FileInput
          attachments={files.attachments}
          isLoading={files.isFilesLoading}
          isUploading={files.isFilesUploading}
          deletingFileName={files.deletingFileName}
          getFileUrl={files.getFileUrl}
          getFileUrlFresh={files.getFileUrlFresh}
          onUpload={files.handleFilesUpload}
          onDelete={files.handleDeleteFile}
          filesDate={files.filesRecord?.updated}
        />
      </div>

      <div className="cm-r-actions">
        {/* ponytail: кнопка удаления временно скрыта по просьбе — вернуть блок при необходимости */}
        <SmsButton leadId={leadId} onNotify={onNotify} />
        <div className="cm-r-actions-sp" />
        <button className="btn-can" onClick={onClose} type="button">Отмена</button>
        <button className="btn-save" type="submit">Сохранить</button>
      </div>
    </form>
  );
};

export { CardModalInfoPane };
